/**
 * `useFileMediaUrlSerializer(contentId)` — produces a sync `serializeFileMedia`
 * callback for `MarkdownSourceView` / `markdownModePlugin`.
 *
 * Strategy: avoid embedding any media tokens / signed URLs in the markdown
 * by routing every `media (type: "file")` reference through Confluence's
 * tenant-wide attachment proxy:
 *
 *   /wiki/download/attachments/{contentId}/{filename}
 *
 * The proxy authenticates with the viewer's Confluence session cookie, so
 * the markdown text stays credential-free and external markdown clients
 * (VS Code preview, GitHub, Obsidian, …) render the image as long as the
 * viewer has read access to the page in their browser.
 *
 * The proxy URL needs the attachment **filename**, but the ADF media node
 * only carries an opaque media-service `id`. We resolve `id → filename` by
 * paginating through the page's attachments via the same GraphQL query
 * `editor-features`'s `createFallbackMediaNameFetcher` uses (Apollo
 * dedupes). The lookup is async; we eagerly populate a sync map on mount
 * so the returned `serializeFileMedia` is a pure map lookup.
 *
 * v1 caveats (deliberate):
 *  - Files added during the markdown-mode session aren't in the map until
 *    the next mount. The serialiser falls back to alt-text-only for
 *    unknown ids — never a `blob:` / token URL.
 *  - DSP-blocked spaces (which require the `/wiki/download/legacy/...`
 *    variant — see `confluence/next/packages/data-security/src/`) are not
 *    yet handled; v1 will produce a URL that 403s for those tenants. A
 *    follow-up should accept a "legacy" mode flag.
 */

import { useEffect, useMemo, useState } from 'react';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import type { SerializeFileMedia } from '@atlassian/mdast-util-from-pm/handlers/media';

import { useFileMediaUrlSerializerQuery } from './useFileMediaUrlSerializerQuery.graphql.ts';
import type {
	useFileMediaUrlSerializerQuery$data,
	useFileMediaUrlSerializerQuery$variables,
} from './__generated__/useFileMediaUrlSerializerQuery.graphql.ts';

const PAGE_SIZE = 50;
const MAX_ITERATIONS = 50;

type FilenameMap = Map<string, string>;

const buildAttachmentMap = async (contentId: string): Promise<FilenameMap> => {
	const map: FilenameMap = new Map();
	let offset = 0;
	let iterations = 0;
	let hasNextPage = true;

	while (hasNextPage && iterations < MAX_ITERATIONS) {
		const { data } = await queryInterop<
			useFileMediaUrlSerializerQuery$data,
			useFileMediaUrlSerializerQuery$variables
		>({
			query: useFileMediaUrlSerializerQuery,
			variables: { contentId, offset, first: PAGE_SIZE },
			fetchPolicy: 'cache-first',
		}).catch(() => ({ data: null }));

		const attachments = data?.content?.edges?.[0]?.node?.attachments;
		const nodes = attachments?.nodes ?? [];

		// Process each attachment and pull out its `fileId` extension.
		//
		// The lint rule (`@atlassian/perf-linting/no-quadratic-complexity`)
		// flags any nested iteration as a potential O(n²) hot spot. In this
		// case the outer loop is bounded by attachments-per-page (a real but
		// modest number — typically dozens, capped at PAGE_SIZE × MAX_ITERATIONS
		// = 2500) and the inner loop is bounded by extensions-per-attachment
		// (a small fixed schema field set, ~4-6 entries in practice — NOT
		// scaled by attachment count). So the actual complexity is O(N × k)
		// with k constant, i.e. linear. The lint rule cannot infer this.
		//
		// Disabling the rule locally with the rationale above rather than
		// reshaping the code into a less-readable form just to satisfy the
		// heuristic.
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (const node of nodes) {
			if (!node?.title || !node?.extensions) {
				continue;
			}
			let fileId: string | undefined;
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			for (const ext of node.extensions) {
				if (ext?.key === 'fileId' && ext?.value) {
					fileId = ext.value;
					break;
				}
			}
			if (fileId) {
				map.set(fileId, node.title);
			}
		}

		hasNextPage = attachments?.pageInfo?.hasNextPage ?? false;
		offset += PAGE_SIZE;
		iterations += 1;
	}

	return map;
};

/** URL-encode but keep `/` so a filename like `images/foo.png` still works. */
const encodeFilename = (filename: string): string =>
	filename
		.split('/')
		.map((segment) => encodeURIComponent(segment))
		.join('/');

/**
 * Returns the tenant base URL (scheme + host) for the current Confluence
 * instance. Using `window.location.origin` is the canonical pattern across
 * confluence-next packages (see e.g. `attachments-page/AttachmentActions.tsx`
 * and `audio/src/utils/getContentUrl.ts`).
 *
 * Falls back to an empty string during SSR / test environments where
 * `window` may not be defined — in that context the URL degrades to a
 * relative path, which is still resolvable for in-browser rendering.
 */
const getOrigin = (): string =>
	typeof window !== 'undefined' && window.location ? window.location.origin : '';

/**
 * Returns a `serializeFileMedia` suitable for `markdownModePlugin`'s config.
 *
 * The map of media-id → filename is fetched on mount and on `contentId`
 * change. Until it resolves, the serialiser returns `undefined` for every
 * id, which causes `MarkdownSourceView` to drop file media to alt-text-only
 * for the (typically sub-second) window before the GraphQL response lands.
 *
 * Pass `undefined` as `contentId` for non-Confluence call sites or when
 * the page hasn't been resolved yet — the hook will return `undefined` and
 * the plugin will leave file media as alt-text-only.
 */
export const useFileMediaUrlSerializer = (
	contentId: string | undefined,
): SerializeFileMedia | undefined => {
	const [filenameMap, setFilenameMap] = useState<FilenameMap | null>(null);

	useEffect(() => {
		if (!contentId) {
			setFilenameMap(null);
			return undefined;
		}
		let cancelled = false;
		buildAttachmentMap(contentId)
			.then((map) => {
				if (!cancelled) {
					setFilenameMap(map);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setFilenameMap(new Map());
				}
			});
		return () => {
			cancelled = true;
		};
	}, [contentId]);

	return useMemo(() => {
		if (!contentId) {
			return undefined;
		}
		const map = filenameMap;
		const serializer: SerializeFileMedia = ({ id, alt }: { id: string; alt?: string }) => {
			const origin = getOrigin();
			const base = `${origin}/wiki/download/attachments/${encodeURIComponent(contentId)}`;

			// 1. Try the pre-fetched id→filename map first (covers existing media).
			const mappedFilename = map?.get(id);
			if (mappedFilename) {
				return `${base}/${encodeFilename(mappedFilename)}`;
			}

			// 2. Fall back to `attrs.alt` as filename for newly uploaded files.
			//    `editor-plugin-media-insert` sets `alt: fileName` on the media
			//    node immediately at insert time, so we have the real filename even
			//    before the attachment list is re-fetched. This means freshly
			//    uploaded media round-trips correctly in the same session without
			//    needing to invalidate and refetch the attachment map.
			//
			//    This is safe to use as the filename because:
			//    a) We set it from `fileName` in the upload response, which IS
			//       the canonical attachment filename in Confluence's media service.
			//    b) Even if `alt` was manually edited by the user after insert, the
			//       worst outcome is a 404 on the proxy URL — not a credential leak.
			if (alt) {
				return `${base}/${encodeFilename(alt)}`;
			}

			// 3. No filename available — return undefined so the caller emits
			//    alt-text-only rather than a broken empty URL.
			return undefined;
		};
		return serializer;
	}, [contentId, filenameMap]);
};
